import axios from 'axios';

import { path, pathOr, has } from 'ramda';

import { getCookie } from '../../commons/cookie';

import { dispatchMetric } from '../../metrics';

import {
  b2wLoginExpectationEvent,
  facebookLoginExpectationEvent,
  googleLoginExpectationEvent
} from '../saika/notify-expectation';
import {
  b2wLoginSuccessfullyEvent,
  facebookLoginSuccessfullyEvent,
  googleLoginSuccessfullyEvent
} from '../saika/notify-fact';

import { B2W } from '../customer/login';

import {
  createAuthenticationUrl,
  customerTokenUrl,
  createGuestAuthenticationUrl,
  createTelemarketerAuthenticationUrl,
  createTelemarketerCustomerAuthenticationUrl,
  createTelemarketerBuyerAuthenticationUrl,
  createGenerateAccessTokenUrl
} from '../api-urls';
import onlyNumbers from '../../commons/utils/only-numbers';
import { getLoginErrorEventPayload } from '../../metrics/metrics.selector';

import config from '../../generated/config';

const {
  show2stepLogin
} = config;


const authenticationRequestPf = ({ email, password, captchaResponse }) => ({
  url: createAuthenticationUrl(captchaResponse),
  method: 'post',
  headers: { 'Content-Type': 'application/vnd.login.b2w+json' },
  data: {
    login: email,
    password
  }
});

const authenticationRequestPj = ({ email, password, cnpj, captchaResponse }) => ({
  url: createAuthenticationUrl(captchaResponse),
  method: 'post',
  headers: { 'Content-Type': 'application/vnd.login.b2w.buyer.cnpj+json' },
  data: {
    login: email,
    cnpj: onlyNumbers(cnpj),
    password
  }
});

const authenticationRequestPj2Step = ({ email, password, captchaResponse }) => ({
  url: createAuthenticationUrl(captchaResponse),
  method: 'post',
  headers: { 'Content-Type': 'application/vnd.login.b2w.buyer+json' },
  data: {
    login: email,
    password
  }
});

const b2wAuthenticationErrorHandler = error => {
  const { httpStatusCode, errorCode, message } = pathOr({}, ['response', 'data'], error);

  switch (httpStatusCode) {
    case 403:
      return Promise.reject({ errorCode });

    case 412:
      return Promise.reject({ errorCode });

    default:
      return Promise.reject({
        errorCode: 'unknown',
        httpStatusCode,
        message
      });
  }
};

export const b2wAuthentication = async authenticationInfo => {
  try {
    b2wLoginExpectationEvent(authenticationInfo.email);
    // TODO: mudar logica de forma a ficar com toggle feature
    // const request = has('cnpj', authenticationInfo) ? authenticationRequestPj(authenticationInfo) : authenticationRequestPf(authenticationInfo);
    let request;

    if (has('cnpj', authenticationInfo) || show2stepLogin) {
      request = authenticationRequestPj(authenticationInfo);

      if (show2stepLogin && !has('cnpj', authenticationInfo)) {
        request = authenticationRequestPj2Step(authenticationInfo);
      }
    } else {
      request = authenticationRequestPf(authenticationInfo);
    }

    const { data } = await axios(request);
    await b2wLoginSuccessfullyEvent(authenticationInfo.email);

    return data;
  } catch (error) {
    return b2wAuthenticationErrorHandler(error);
  }
};

const facebookAuthenticationRequest = token => ({
  url: customerTokenUrl,
  method: 'post',
  headers: {
    'Content-Type': 'application/vnd.login.facebook+json'
  },
  data: {
    token
  }
});

const googleAuthenticationRequest = idToken => ({
  url: customerTokenUrl,
  method: 'post',
  headers: {
    'Content-Type': 'application/vnd.login.google+json'
  },
  data: {
    idToken
  }
});

export const facebookAuthentication = async authenticationInfo => {
  try {
    facebookLoginExpectationEvent();
    const accessToken = path(['authResponse', 'accessToken'], authenticationInfo);
    const requestObject = facebookAuthenticationRequest(accessToken);
    const {
      data: { login, ...rest }
    } = await axios(requestObject);
    await facebookLoginSuccessfullyEvent();

    return { ...rest, email: login };
  } catch (err) {
    return Promise.reject(err);
  }
};

export const googleAuthentication = async authenticationInfo => {
  try {
    googleLoginExpectationEvent();
    const accessToken = path(['id_token'], authenticationInfo);
    const requestObject = googleAuthenticationRequest(accessToken);
    const {
      data: { login, ...rest }
    } = await axios(requestObject);
    await googleLoginSuccessfullyEvent();

    return { ...rest, email: login };
  } catch (err) {
    return Promise.reject(err);
  }
};

const guestAuthenticationRequest = ({ orderId, cpf }) => ({
  url: createGuestAuthenticationUrl(cpf),
  method: 'post',
  data: {
    orderId
  }
});

export const guestAuthentication = async authenticationInfo => {
  try {
    const requestObject = guestAuthenticationRequest(authenticationInfo);
    const {
      data: { eloId: id, ...rest }
    } = await axios(requestObject);
    return { ...rest, id };
  } catch (err) {
    return Promise.reject(err);
  }
};

const telemarketerAuthenticationRequest = ({ username, password }) => ({
  url: createTelemarketerAuthenticationUrl(username),
  method: 'post',
  data: {
    password
  }
});

export const telemarketerAuthentication = async authenticationInfo => {
  try {
    const requestObject = telemarketerAuthenticationRequest(authenticationInfo);
    const { status, data } = await axios(requestObject);
    return { status, ...data };
  } catch (error) {
    return Promise.reject(error);
  }
};

const telemarketerCustomerAuthenticationRequest = ({ username }) => ({
  url: createTelemarketerCustomerAuthenticationUrl(),
  method: 'post',
  data: {
    customerLogin: username,
    token: getCookie('telemarketerToken')
  }
});

export const telemarketerCustomerAuthentication = async authenticationInfo => {
  try {
    const requestObject = telemarketerCustomerAuthenticationRequest(authenticationInfo);
    const { status, data } = await axios(requestObject);
    return { status, ...data };
  } catch (error) {
    return Promise.reject(error);
  }
};

const telemarketerBuyerAuthenticationRequest = ({ email, cnpj }) => ({
  url: createTelemarketerBuyerAuthenticationUrl(),
  method: 'post',
  data: {
    login: email,
    cnpj: cnpj,
    token: getCookie('telemarketerToken')
  }
});

export const telemarketerBuyerAuthentication = async authenticationInfo => {
  try {
    const requestObject = telemarketerBuyerAuthenticationRequest(authenticationInfo);
    const { status, data } = await axios(requestObject);
    return { status, ...data };
  } catch (error) {
    return Promise.reject(error);
  }
};

const generateAccessTokenRequest = ({ customerId, code }) => ({
  url: createGenerateAccessTokenUrl(),
  method: 'post',
  headers: {
    'Content-Type': 'application/vnd.login.client+json'
  },
  data: {
    customerId,
    code
  }
});

export const generateAccessToken = async authenticationInfo => {
  try {
    const requestObject = generateAccessTokenRequest(authenticationInfo);
    const { status, data } = await axios(requestObject);

    console.log(status);
    console.log('after calling customer-token Id:' + data.id);
    console.log('generated Token:' + data.token);
    return { status, ...data };
  } catch (error) {
    return Promise.reject(error);
  }
};

export function dispatchErrorEvent(error) {
  try {
    dispatchMetric('login:error', { error, type: B2W, payload: getLoginErrorEventPayload() });
  } catch (error) {
    console.error(`Error in dispatchEvents`, error);
  }
}
